'use client';

import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);
gsap.config({ nullTargetWarn: false });

/**
 * GSAP conductor, mounted on every page. Server components stay untouched —
 * everything is driven through selectors, and scenes simply no-op on pages
 * where their targets don't exist. On mount it stamps `.gsap-anim` on <html>,
 * which neutralizes the CSS `.reveal` fallback, then takes over.
 *
 * Reveal engine: any `.reveal` element outside the hero animates in on scroll.
 * A `data-anim` attribute picks the variant:
 *   (none)  → fade-up          "left"  → slide from the left
 *   "pop"   → scale-in         "title" → handled by a SplitText scene
 *   "none"  → owned by another scene (e.g. eyebrows)
 * `.delay-N` classes add N × 0.08s, for staggering siblings.
 *
 * On top of that: hero load timeline, split-text typography scenes,
 * velocity-reactive marquee, nav auto-hide, scroll progress, and on
 * desktop a custom cursor, magnetic buttons, and 3D card tilt.
 */
export default function GsapStage() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('gsap-anim');

    const mm = gsap.matchMedia();

    mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        fine: '(hover: hover) and (pointer: fine)',
      },
      (ctx) => {
        const { motion, fine } = ctx.conditions as {
          motion: boolean;
          fine: boolean;
        };
        // Reduced motion: .gsap-anim already shows everything statically.
        if (!motion) return;

        const splits: SplitText[] = [];
        const disposers: (() => void)[] = [];
        const hero = document.querySelector('#hero');

        /* Hide hero targets before first paint — same tick as the
           .gsap-anim stamp, so there is no flash of unanimated content. */
        if (hero) {
          gsap.set(
            ['#hero .eyebrow', '#hero-heading', '#hero .lead', '.hero-cta', '.hero-side'],
            { autoAlpha: 0 }
          );
        }

        /* ---------------- scroll progress ---------------- */
        if (barRef.current) {
          gsap.to(barRef.current, {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
          });
        }

        /* ---------------- nav: hide on scroll down ---------------- */
        const navHide = gsap.to('.nav', {
          yPercent: -110,
          duration: 0.35,
          ease: 'power2.inOut',
          paused: true,
        });
        ScrollTrigger.create({
          start: 'top -160',
          end: 'max',
          onUpdate: (self) =>
            self.direction === 1 ? navHide.play() : navHide.reverse(),
          onLeaveBack: () => navHide.reverse(),
        });

        /* ---------------- marquee: velocity-reactive loop ---------------- */
        const track = document.querySelector('.marquee-track');
        if (track) {
          const marqueeLoop = gsap.to(track, {
            xPercent: -50,
            ease: 'none',
            duration: 30,
            repeat: -1,
          });
          ScrollTrigger.create({
            start: 0,
            end: 'max',
            onUpdate: (self) => {
              const boost = gsap.utils.clamp(-5, 5, self.getVelocity() / 350);
              const ts = Math.abs(boost) < 1 ? (boost < 0 ? -1 : 1) : boost;
              gsap.to(marqueeLoop, { timeScale: ts, duration: 0.3, overwrite: true });
            },
          });
          const onScrollEnd = () =>
            gsap.to(marqueeLoop, { timeScale: 1, duration: 0.6 });
          ScrollTrigger.addEventListener('scrollEnd', onScrollEnd);
          disposers.push(() =>
            ScrollTrigger.removeEventListener('scrollEnd', onScrollEnd)
          );
        }

        /* ---------------- hero: parallax out + glow ---------------- */
        if (hero) {
          gsap
            .timeline({
              scrollTrigger: {
                trigger: '#hero',
                start: 'top top',
                end: 'bottom top',
                scrub: true,
              },
            })
            .to('.hero-copy', { yPercent: -16, opacity: 0.3, ease: 'none' }, 0)
            .to('.hero-side', { yPercent: -32, opacity: 0.25, ease: 'none' }, 0)
            .to('.hero-glow', { yPercent: 30, ease: 'none' }, 0);

          gsap.fromTo(
            '.hero-glow',
            { opacity: 0.65 },
            { opacity: 1, duration: 5, ease: 'sine.inOut', repeat: -1, yoyo: true }
          );
        }

        /* ================================================================
           Reveal engine — every .reveal outside the hero, variant by
           data-anim. Runs synchronously so pre-hidden content never flashes.
           ================================================================ */
        gsap.utils.toArray<HTMLElement>('.reveal').forEach((el) => {
          if (el.closest('#hero')) return; // hero intro owns these
          if (el.matches('.project-row')) return; // wipe scene owns these
          const kind = el.dataset.anim ?? 'fade';
          if (kind === 'none') return; // another scene owns it
          if (kind === 'title') {
            // SplitText scene reveals it after fonts load
            gsap.set(el, { autoAlpha: 0 });
            return;
          }
          const delayMatch = /(?:^|\s)delay-(\d)/.exec(el.className);
          const delay = delayMatch ? +delayMatch[1] * 0.08 : 0;
          const st = { trigger: el, start: 'top 88%' };
          if (kind === 'left') {
            gsap.from(el, {
              x: -60,
              autoAlpha: 0,
              duration: 0.9,
              ease: 'power3.out',
              delay,
              scrollTrigger: st,
            });
          } else if (kind === 'pop') {
            gsap.from(el, {
              scale: 0.92,
              y: 24,
              autoAlpha: 0,
              duration: 0.9,
              ease: 'back.out(1.6)',
              delay,
              scrollTrigger: st,
            });
          } else {
            gsap.from(el, {
              y: 32,
              autoAlpha: 0,
              duration: 0.9,
              ease: 'power3.out',
              delay,
              scrollTrigger: st,
            });
          }
        });

        /* ---------------- eyebrows: tick in from the left ---------------- */
        gsap.utils.toArray<HTMLElement>('.eyebrow').forEach((el) => {
          if (el.closest('#hero')) return;
          gsap.from(el, {
            x: -32,
            autoAlpha: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%' },
          });
        });

        /* ---------------- project rows: wipe in per list ---------------- */
        gsap.utils.toArray<HTMLElement>('.projects-list').forEach((list) => {
          const rows = gsap.utils.toArray<HTMLElement>('.project-row', list);
          if (!rows.length) return;
          gsap.fromTo(
            rows,
            { clipPath: 'inset(0 100% 0 0)', x: -36 },
            {
              clipPath: 'inset(0 0% 0 0)',
              x: 0,
              duration: 1.1,
              ease: 'expo.out',
              stagger: 0.12,
              scrollTrigger: { trigger: list, start: 'top 82%' },
              onComplete: () => gsap.set(rows, { clearProps: 'clip-path,transform' }),
            }
          );
        });

        /* ---------------- about (index): body + meta ---------------- */
        gsap.from('#about .about-grid > div:last-child > p:not(.about-quote)', {
          y: 30,
          autoAlpha: 0,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.15,
          scrollTrigger: { trigger: '#about .about-grid', start: 'top 70%' },
        });
        gsap.from('.about-meta > div', {
          y: 26,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.1,
          scrollTrigger: { trigger: '.about-meta', start: 'top 90%' },
        });

        /* ---------------- skills (index): the graph assembles ------------ */
        const svg = document.querySelector('#skills svg');
        if (svg) {
          const nodeSel = '.tg-center, .tg-cat, .tg-tech';
          const edgeSel = '.tg-cat-edge, .tg-tech-edge, .tg-cross';
          const graph = gsap.timeline({
            scrollTrigger: { trigger: '#skills', start: 'top 62%' },
            defaults: { transformOrigin: '50% 50%' },
            onComplete: () => {
              // hand style control back to React's hover logic
              gsap.set(svg.querySelectorAll(`${nodeSel}, ${edgeSel}, .tg-glow`), {
                clearProps: 'all',
              });
              gsap.fromTo(
                '.tg-glow',
                { opacity: 0.7 },
                { opacity: 1, duration: 4, ease: 'sine.inOut', repeat: -1, yoyo: true }
              );
            },
          });
          graph
            .from('.tg-glow', { scale: 0, opacity: 0, duration: 1, ease: 'power2.out' }, 0)
            .from('.tg-center', { scale: 0, duration: 0.9, ease: 'back.out(1.8)' }, 0.05)
            .from('.tg-cat-edge', { opacity: 0, duration: 0.5, stagger: 0.07 }, 0.4)
            .from(
              '.tg-cat',
              { scale: 0, duration: 0.7, ease: 'back.out(2)', stagger: 0.08 },
              0.55
            )
            .from('.tg-tech-edge', { opacity: 0, duration: 0.4, stagger: 0.02 }, 0.9)
            .from(
              '.tg-tech',
              {
                scale: 0,
                duration: 0.6,
                ease: 'back.out(1.7)',
                stagger: { each: 0.025, from: 'random' },
              },
              1.0
            )
            .from('.tg-cross', { opacity: 0, duration: 0.5 }, 1.5);
        }

        /* ---------------- certifications (index): card lands in 3D ------- */
        if (document.querySelector('.cert-card')) {
          gsap.from('.cert-card', {
            rotationY: 14,
            y: 48,
            autoAlpha: 0,
            transformPerspective: 900,
            duration: 1.2,
            ease: 'expo.out',
            scrollTrigger: { trigger: '.cert-card', start: 'top 82%' },
          });
        }

        /* ---------------- FAQ (index): cascade ---------------- */
        const faqItems = gsap.utils.toArray<HTMLElement>('.faq-item:not(.reveal)');
        if (faqItems.length) {
          gsap.set(faqItems, { y: 30, autoAlpha: 0 });
          ScrollTrigger.batch(faqItems, {
            start: 'top 90%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, {
                y: 0,
                autoAlpha: 1,
                duration: 0.8,
                ease: 'power3.out',
                stagger: 0.09,
              }),
          });
        }

        /* ---------------- contact (index): buttons + socials ------------- */
        gsap.from('#contact .btn', {
          y: 26,
          autoAlpha: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.1,
          scrollTrigger: { trigger: '#contact', start: 'top 60%' },
        });
        gsap.from('#contact nav a', {
          y: 18,
          autoAlpha: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.07,
          scrollTrigger: { trigger: '#contact nav', start: 'top 94%' },
        });

        /* ---------------- case study: cover image reveal ----------------- */
        /* No scale/pan parallax: the cover is a composed shot with content
           at the edges, and any zoom inside the overflow-hidden frame crops it. */
        if (document.querySelector('.cs-cover-img')) {
          gsap.from('.cs-cover-img', {
            autoAlpha: 0,
            duration: 0.9,
            ease: 'power2.out',
            scrollTrigger: { trigger: '.cs-cover', start: 'top 85%' },
          });
        }

        /* ================================================================
           Typography scenes wait for fonts so SplitText measures real
           glyphs. `data-anim="title"` elements stay hidden until here.
           ================================================================ */
        const build = () => {
          /* ---------- hero intro timeline (index only) ---------- */
          if (hero) {
            const titleSplit = new SplitText('.hero-title-line', { type: 'chars', aria: 'none' });
            const commentSplit = new SplitText('.hero-comment', { type: 'chars', aria: 'none' });
            const heroLead = new SplitText('#hero .lead', { type: 'lines', mask: 'lines' });
            const cmdSplits = gsap.utils
              .toArray<HTMLElement>('.terminal .t-cmd')
              .map((el) => new SplitText(el, { type: 'chars', aria: 'none' }));
            splits.push(titleSplit, commentSplit, heroLead, ...cmdSplits);

            const chars = titleSplit.chars;
            const dot = chars[chars.length - 1];
            const tlines = gsap.utils.toArray<HTMLElement>('.terminal .tline');

            const intro = gsap.timeline({ defaults: { ease: 'expo.out' } });
            intro
              .set(commentSplit.chars, { autoAlpha: 0 })
              .set(tlines, { autoAlpha: 0 })
              .set(
                cmdSplits.flatMap((s) => s.chars),
                { autoAlpha: 0 }
              )
              .set(
                ['#hero-heading', '#hero .lead', '.hero-cta', '.hero-side', '#hero .eyebrow'],
                { autoAlpha: 1 }
              )
              .from('#hero .eyebrow', { x: -28, autoAlpha: 0, duration: 0.7 }, 0.05)
              .from(
                chars.slice(0, -1),
                { yPercent: 140, rotation: 9, autoAlpha: 0, duration: 1.1, stagger: 0.055 },
                0.1
              )
              .from(
                dot,
                { scale: 0, yPercent: 60, autoAlpha: 0, duration: 0.8, ease: 'back.out(2.5)' },
                0.72
              )
              .to(
                commentSplit.chars,
                { autoAlpha: 1, duration: 0.01, stagger: 0.018, ease: 'none' },
                1.0
              )
              .from('#hero .lead', { autoAlpha: 0, duration: 0.01 }, 0.55)
              .from(heroLead.lines, { yPercent: 120, duration: 1, stagger: 0.09 }, 0.55)
              .from('.hero-cta > *', { y: 26, autoAlpha: 0, duration: 0.8, stagger: 0.1 }, 0.85)
              .from('.terminal', { y: 44, scale: 0.96, autoAlpha: 0, duration: 1 }, 0.45)
              // terminal boot: commands type, outputs print
              .to(tlines[0], { autoAlpha: 1, duration: 0.15 }, 1.2)
              .to(
                cmdSplits[0]?.chars ?? [],
                { autoAlpha: 1, duration: 0.01, stagger: 0.06, ease: 'none' },
                1.3
              )
              .to(tlines[1], { autoAlpha: 1, duration: 0.2 }, 1.85)
              .to(tlines[2], { autoAlpha: 1, duration: 0.15 }, 2.1)
              .to(
                cmdSplits[1]?.chars ?? [],
                { autoAlpha: 1, duration: 0.01, stagger: 0.045, ease: 'none' },
                2.2
              )
              .to(tlines[3], { autoAlpha: 1, duration: 0.25 }, 2.95)
              .to(tlines[4], { autoAlpha: 1, duration: 0.2 }, 3.3)
              .from('.hero-badge', { y: 18, autoAlpha: 0, duration: 0.7 }, 2.6);
          }

          /* ---------- section titles: masked word rise ---------- */
          gsap.utils
            .toArray<HTMLElement>('h1.section-title, h2.section-title')
            .forEach((el) => {
              const s = new SplitText(el, { type: 'words', mask: 'words' });
              splits.push(s);
              gsap.set(el, { autoAlpha: 1 });
              gsap.from(s.words, {
                yPercent: 130,
                rotation: 4,
                duration: 1,
                ease: 'expo.out',
                stagger: 0.07,
                scrollTrigger: { trigger: el, start: 'top 86%' },
              });
            });

          /* ---------- display headings outside the hero: line rise ------- */
          gsap.utils.toArray<HTMLElement>('.display').forEach((el) => {
            if (el.closest('#hero')) return;
            const s = new SplitText(el, { type: 'lines', mask: 'lines' });
            splits.push(s);
            gsap.set(el, { autoAlpha: 1 });
            gsap.from(s.lines, {
              yPercent: 115,
              duration: 1.2,
              ease: 'expo.out',
              stagger: 0.11,
              scrollTrigger: { trigger: el, start: 'top 80%' },
            });
          });

          /* ---------- leads outside the hero: masked line rise ---------- */
          gsap.utils.toArray<HTMLElement>('main p.lead').forEach((el) => {
            if (el.closest('#hero')) return;
            const s = new SplitText(el, { type: 'lines', mask: 'lines' });
            splits.push(s);
            gsap.set(el, { autoAlpha: 1 });
            gsap.from(s.lines, {
              yPercent: 110,
              duration: 0.9,
              ease: 'expo.out',
              stagger: 0.08,
              scrollTrigger: { trigger: el, start: 'top 85%' },
            });
          });

          /* ---------- about (index): quote fills word by word ------------ */
          const quote = document.querySelector('.about-quote');
          if (quote) {
            const q = new SplitText(quote, { type: 'words' });
            splits.push(q);
            gsap.fromTo(
              q.words,
              { opacity: 0.12 },
              {
                opacity: 1,
                ease: 'none',
                stagger: 0.15,
                scrollTrigger: {
                  trigger: quote,
                  start: 'top 78%',
                  end: 'bottom 42%',
                  scrub: true,
                },
              }
            );
            gsap.from('.dropcap', {
              scale: 0,
              rotation: -15,
              duration: 0.9,
              ease: 'back.out(2)',
              scrollTrigger: { trigger: quote, start: 'top 80%' },
            });
          }

          ScrollTrigger.refresh();
        };

        // Hero content stays hidden until this fires — on a slow connection
        // document.fonts.ready alone can stall for seconds (a big chunk of
        // measured LCP render delay), so cap the wait rather than block on it.
        const fontsReady = document.fonts.ready.catch(() => {});
        const fontsTimeout = new Promise<void>((resolve) => setTimeout(resolve, 500));
        Promise.race([fontsReady, fontsTimeout]).then(() => ctx.add(build));

        /* ================================================================
           Desktop-only flourishes: cursor, magnetic buttons, card tilt
           ================================================================ */
        if (fine) {
          /* ---------- custom cursor ---------- */
          const cursorEls = [dotRef.current, ringRef.current].filter(Boolean) as HTMLDivElement[];
          if (cursorEls.length === 2) {
            root.classList.add('gsap-cursor');
            const [cDot, cRing] = cursorEls;
            gsap.set(cursorEls, { xPercent: -50, yPercent: -50 });
            const dX = gsap.quickTo(cDot, 'x', { duration: 0.12, ease: 'power2.out' });
            const dY = gsap.quickTo(cDot, 'y', { duration: 0.12, ease: 'power2.out' });
            const rX = gsap.quickTo(cRing, 'x', { duration: 0.45, ease: 'power3.out' });
            const rY = gsap.quickTo(cRing, 'y', { duration: 0.45, ease: 'power3.out' });
            let shown = false;

            const onMove = (e: MouseEvent) => {
              dX(e.clientX);
              dY(e.clientY);
              rX(e.clientX);
              rY(e.clientY);
              if (!shown) {
                shown = true;
                gsap.to(cursorEls, { autoAlpha: 1, duration: 0.25 });
              }
            };
            const onOver = (e: MouseEvent) => {
              const t = (e.target as Element).closest?.(
                'a, button, .project-row, .terminal, .cert-card'
              );
              const isRow = !!t && (t as Element).classList.contains('project-row');
              gsap.to(cRing, { scale: t ? (isRow ? 2.6 : 1.7) : 1, duration: 0.35 });
              gsap.to(cDot, { scale: t ? 0.4 : 1, duration: 0.35 });
              cRing.classList.toggle('gc-active', !!t);
            };
            const onLeave = () => {
              shown = false;
              gsap.to(cursorEls, { autoAlpha: 0, duration: 0.25 });
            };
            const onDown = () => gsap.to(cRing, { scale: 0.8, duration: 0.15 });
            const onUp = () => gsap.to(cRing, { scale: 1, duration: 0.3, ease: 'back.out(2)' });

            window.addEventListener('mousemove', onMove, { passive: true });
            document.addEventListener('mouseover', onOver);
            document.documentElement.addEventListener('mouseleave', onLeave);
            window.addEventListener('mousedown', onDown);
            window.addEventListener('mouseup', onUp);
            disposers.push(() => {
              window.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseover', onOver);
              document.documentElement.removeEventListener('mouseleave', onLeave);
              window.removeEventListener('mousedown', onDown);
              window.removeEventListener('mouseup', onUp);
              root.classList.remove('gsap-cursor');
            });
          }

          /* ---------- magnetic buttons ---------- */
          gsap.utils.toArray<HTMLElement>('.btn, .contra-fab').forEach((el) => {
            const xTo = gsap.quickTo(el, 'x', { duration: 0.35, ease: 'power3.out' });
            const yTo = gsap.quickTo(el, 'y', { duration: 0.35, ease: 'power3.out' });
            // Rect is read once on enter (before any transform write) and reused for the
            // whole hover so mousemove never forces a layout read after quickTo's write.
            let rect: DOMRect | null = null;
            const onEnter = () => {
              rect = el.getBoundingClientRect();
            };
            const onEnterMove = (e: MouseEvent) => {
              if (!rect) rect = el.getBoundingClientRect();
              xTo((e.clientX - (rect.left + rect.width / 2)) * 0.32);
              yTo((e.clientY - (rect.top + rect.height / 2)) * 0.32);
            };
            const onOut = () => {
              rect = null;
              gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.45)' });
            };
            el.addEventListener('mouseenter', onEnter);
            el.addEventListener('mousemove', onEnterMove);
            el.addEventListener('mouseleave', onOut);
            disposers.push(() => {
              el.removeEventListener('mouseenter', onEnter);
              el.removeEventListener('mousemove', onEnterMove);
              el.removeEventListener('mouseleave', onOut);
            });
          });

          /* ---------- cert card: 3D tilt ---------- */
          const card = document.querySelector<HTMLElement>('.cert-card');
          if (card) {
            gsap.set(card, { transformPerspective: 900 });
            const rotX = gsap.quickTo(card, 'rotationX', { duration: 0.5, ease: 'power2.out' });
            const rotY = gsap.quickTo(card, 'rotationY', { duration: 0.5, ease: 'power2.out' });
            // Same reflow-avoidance as the magnetic buttons above: cache the rect on enter.
            let rect: DOMRect | null = null;
            const onTiltEnter = () => {
              rect = card.getBoundingClientRect();
            };
            const onTilt = (e: MouseEvent) => {
              if (!rect) rect = card.getBoundingClientRect();
              const nx = (e.clientX - rect.left) / rect.width - 0.5;
              const ny = (e.clientY - rect.top) / rect.height - 0.5;
              rotY(nx * 10);
              rotX(ny * -8);
            };
            const onTiltOut = () => {
              rect = null;
              rotX(0);
              rotY(0);
            };
            card.addEventListener('mouseenter', onTiltEnter);
            card.addEventListener('mousemove', onTilt);
            card.addEventListener('mouseleave', onTiltOut);
            disposers.push(() => {
              card.removeEventListener('mouseenter', onTiltEnter);
              card.removeEventListener('mousemove', onTilt);
              card.removeEventListener('mouseleave', onTiltOut);
            });
          }
        }

        return () => {
          disposers.forEach((d) => d());
          splits.forEach((s) => s.revert());
        };
      }
    );

    return () => {
      mm.revert();
      root.classList.remove('gsap-anim');
    };
  }, []);

  return (
    <>
      <div ref={barRef} className="scroll-progress" aria-hidden="true" />
      <div ref={dotRef} className="gc-dot" aria-hidden="true" />
      <div ref={ringRef} className="gc-ring" aria-hidden="true" />
    </>
  );
}
